import {
  forwardRef,
  type ReactElement,
  type ReactNode,
  type Ref,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import classNames from "classnames";
import { IconClickable } from "../icon/IconClickable";
import { IconType } from "../icon/IconType";
import { IconSize } from "../icon/IconSize";
import chunk from "lodash/chunk";
import { useIsMinDesktopSize } from "../../hooks/useMediaQuery";

import classes from "./Carousel.module.less";
import { ReactSwipe, type ReactSwipeRef } from "./ReactSwipe";

export interface CarouselProps<T> {
  className?: string;
  /**
   * Objects to render
   */
  data: T[];
  /**
   * Number of items per slide / page
   */
  pageItemsCount: number;
  renderSlide: (props: CarouselSlideProps<T>) => ReactNode;
  /**
   * Where should the controls be placed.
   * Below - below like in classic carousels
   * Inside - inside the image as required by https://agile.deloitte.de/browse/SCHAEFF-15792
   */
  controlsVariant?: "below" | "inside";

  continuous?: boolean;
  paginationItemClassName?: string;
  paginationItemActiveClassName?: string;
  hideArrows?: boolean;
}

export interface CarouselRefAttributes {
  getActivePage: () => number;
  getPageAmount: () => number;
  setActivePage: (index: number) => void;
  setNextPage: () => void;
  setPreviousPage: () => void;
}

interface CarouselSlideProps<T> {
  data: T[];
}

function Cmp<T>(props: CarouselProps<T>, ref: Ref<CarouselRefAttributes>) {
  const swipeRef = useRef<ReactSwipeRef>(null);
  const isDesktop = useIsMinDesktopSize();

  const {
    className,
    renderSlide,
    data,
    pageItemsCount,
    paginationItemClassName,
    paginationItemActiveClassName,
    hideArrows = false,
  } = props;

  const pages = useMemo(() => chunk(data, pageItemsCount), [data, pageItemsCount]);

  const [internalActivePage, setInternalInternalActivePage] = useState(0);

  const isFirstPage = internalActivePage === 0;
  const isLastPage = internalActivePage === pages.length - 1;

  const setPreviousPage = () => swipeRef.current?.prev();
  const setNextPage = () => swipeRef.current?.next();
  const setActivePage = (index: number) => swipeRef.current?.slide(index, 500);

  useImperativeHandle(ref, () => ({
    getActivePage: () => swipeRef.current?.getPos() ?? 0,
    getPageAmount: () => pages.length,
    setActivePage,
    setNextPage,
    setPreviousPage,
  }));

  const controlsVariant = props.controlsVariant ?? "below";
  const controlsCls = controlsVariant === "below" ? classes.controlsClassic : classes.controlsInside;

  const [swipeOptions] = useState(() => ({
    callback: setInternalInternalActivePage,
    continuous: props.continuous ?? false,
    speed: 500,
  }));

  return (
    <div className={classNames(classes.root, className)}>
      <ReactSwipe childCount={pages.length} ref={swipeRef} className={"scha__carousel"} swipeOptions={swipeOptions}>
        {(style) =>
          pages.map((slideData, index) => (
            <div key={`slide-${index}`} className="scha__carousel__row" style={style}>
              {renderSlide({ data: slideData })}
            </div>
          ))
        }
      </ReactSwipe>
      {pages.length > 1 && (
        <div className={classNames(controlsCls, "controls-container-margin")}>
          {isDesktop && !hideArrows && (
            <IconClickable
              className={classes.leftArrow}
              type={IconType.ChevronLeft}
              size={IconSize.FONT_16}
              disabled={isFirstPage && !props.continuous}
              onIconClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setPreviousPage();
              }}
            />
          )}
          <div className={classes.pagination}>
            {pages.map((_, index) => (
              <div
                key={`carousel-control-${index}`}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setActivePage(index);
                }}
                className={classNames(
                  paginationItemClassName ?? classes.paginationItem,
                  internalActivePage === index && (paginationItemActiveClassName ?? classes.paginationItemActive),
                )}
              />
            ))}
          </div>
          {isDesktop && !hideArrows && (
            <IconClickable
              className={classes.rightArrow}
              type={IconType.ChevronRight}
              size={IconSize.FONT_16}
              disabled={isLastPage && !props.continuous}
              onIconClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setNextPage();
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

export const Carousel = forwardRef(Cmp) as <T extends object>(
  props: CarouselProps<T> & { ref?: Ref<CarouselRefAttributes> },
) => ReactElement;
